import React from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useParams, Navigate } from 'react-router-dom';
import AuthProvider from './contexts/AuthContext';
import { ProgressProvider } from './contexts/ProgressContext'; // Add this import
// import Welcome from './components/Welcome/Welcome'; // Commented out - no longer needed
import Home from './components/Home/Home';
import Grammar from './components/Grammar/Grammar';
import GrammarFlashcards from './components/Grammar/Flashcards';
import Detective from './components/Grammar/Detective';
import GrammarDefender from './components/Grammar/GrammarDefender';
import VocabularyFlashcards from './components/Vocabulary/VocabularyFlashcards/VocabularyFlashcards';
import Vocabulary from './components/Vocabulary/Vocabulary';
import WordFactory from './components/Vocabulary/WordFactory';
import DragonRaceTyping from './components/Vocabulary/DragonRaceTyping';
import Storybook from './components/Storybook/Storybook';
import GraphicNovelEditor from './components/Storybook/GraphicNovelEditor/GraphicNovelEditor';
import PictureBookEditor from './components/Storybook/PictureBookEditor';
import StoryStarter from './components/Storybook/StoryStarter';
import ChapterMaster from './components/Storybook/ChapterMaster';
import DailyWriter from './components/Storybook/DailyWriter';
import StoryArcBuilder from './components/Storybook/StoryArcBuilder';
// Import CrosswordModule as default and SinglePuzzle as a named export
import CrosswordModule, { SinglePuzzle } from './components/Vocabulary/CrosswordModule';
import VocabularyMemory from './components/Vocabulary/VocabularyMemory/VocabularyMemory';
import Navbar from './components/Navbar/Navbar';
import LandingNavbar from './components/Navbar/LandingNavbar';
import { useAuth } from './contexts/AuthContext';
import { useLocation } from 'react-router-dom';
import RegularBookEditor from './components/Storybook/RegularBookEditor';
import Prompts from './components/Prompts/Prompts';
import CoverDesigner from './components/Storybook/CoverDesigner';
import EnhancedStoryPlanner from './components/Storybook/EnhancedStoryPlanner';
import CharacterSheet from './components/Storybook/CharacterSheet';
import FeaturedStories from './components/FeaturedStories/FeaturedStories';
import FormatSelection from './components/Storybook/FormatSelection';
import GamesHub from './components/GamesHub/GamesHub';
import Account from './components/Account/Account';
import Signup from './components/Account/Signup';
import Login from './components/Account/Login';
import SignupEducator from './components/Account/SignupEducator';
import Dashboard from './components/Dashboard/Dashboard';
import Profile from './components/Account/Profile';
import StoryDetail from './components/StoryDetail/StoryDetail';
import StoryReader from './components/StoryReader/StoryReader';
import HelpSupport from './components/Account/HelpSupport/HelpSupport';
import TestDataGenerator from './components/TestDataGenerator/TestDataGenerator';
import PrivacyPolicy from './components/Account/HelpSupport/PrivacyPolicy';
import TermsService from './components/Account/HelpSupport/TermsService';
import SafetyCenter from './components/Account/HelpSupport/SafetyCenter';
import CommunityGuidelines from './components/Account/HelpSupport/CommunityGuidelines';
import MyStories from './components/Stories/MyStories';
import MyCards from './components/Cards/MyCards';
//import { GuestModeTestingPanel } from './components/dev/GuestModeTestingPanel';
import About from './components/Pages/About';
import Contact from './components/Pages/Contact';
import TermsOfService from './components/Pages/TermsofService';
import ForParents from './components/Pages/ForParents';
import ForTeachers from './components/Pages/ForTeachers';
import TeacherDashboard from './components/Teacher/TeacherDashboard';
import TeacherAssignments from './components/Teacher/TeacherAssignments';
import TeacherClasses from './components/Teacher/TeacherClasses';
import TeacherFeedback from './components/Teacher/TeacherFeedback';
import ClassDetail from './components/Teacher/ClassDetail';
import StudentSubmissions from './components/Teacher/StudentSubmissions';
import StoryViewer from './components/Teacher/StoryViewer';
import AssignmentSubmissions from './components/Teacher/AssignmentSubmissions';
import ForSchools from './components/Marketing/ForSchools';
import BlogList from './components/Blog/BlogList';
import BlogPost from './components/Blog/BlogPost';
import Footer from './components/Home/Footer';
import { StoryProvider } from './contexts/StoryContext';
import './components/styles/deferredRegistration.css';
import FreeResources from './components/Resources/FreeResources';
import './App.css';
import AuthorProfile from './components/Account/AuthorProfile';
   
// Define prop interfaces for components that need them
interface NavigationProps {
  onNavigateBack?: () => void;
  onNavigateHome?: () => void;
}

// Navigation component that uses React Router's hooks properly
const NavigationHandler: React.FC = () => {
    const navigate = useNavigate();
    const { currentUser } = useAuth();
    const location = useLocation();
    const isLandingPage = location.pathname === '/';
    const isAuthPage = location.pathname === '/signup' || location.pathname === '/login';
    
    const navigateToSignup = () => navigate('/signup');
    const navigateToGrammar = () => navigate('/grammar');
    const navigateToVocabulary = () => navigate('/vocabulary');
    const navigateToStorybook = () => navigate('/storybook');
    const navigateToHome = () => navigate('/');
    const navigateToFeaturedStories = () => navigate('/featured-stories');
    const navigateToAccount = () => navigate('/account');

    const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
        const { currentUser } = useAuth();
        return currentUser ? <>{children}</> : <Navigate to="/login" />;
    };
    
    
    // Add navigation functions for the book editors
    const navigateToStoryPlanner = () => navigate('/story-planner');
    const navigateToChapterBook = () => navigate('/regular-book-editor');
    const navigateToGraphicNovel = () => navigate('/graphic-novel-editor');
    const navigateToPictureBook = () => navigate('/picture-book-editor');
    const navigateToStoryStarter = () => navigate('/story-starter');
    const navigateToChapterMaster = () => navigate('/chapter-master');  
    const navigateToDailyWriter = () => navigate('/daily-writer');
    const navigateToStoryArcBuilder = () => navigate('/story-arc-builder');
    
    // Add navigation functions for vocabulary games
    const navigateToVocabularyFlashcards = () => navigate('/vocabulary-flashcards');
    const navigateToVocabularyMemory = () => navigate('/vocabulary-memory');
    const navigateToCrosswords = () => navigate('/crosswords');
    const navigateToWordFactory = () => navigate('/word-factory');
    const navigateToDragonRacing = () => navigate('/dragon-racing');

    const StoryDetailWrapper: React.FC = () => {
        const { storyId } = useParams<{ storyId: string }>();
        const navigate = useNavigate();
        
        
        // In a real app, fetch story data by ID
        // For now, mock data:
        const story = {
            title: "The Magic Dragon",
            author: "Sarah",
            age: 9,
            cover: "🐉",
            reads: "2.3k",
            description: "A brave knight befriends a lonely dragon in this heartwarming adventure about friendship and courage.",
            genre: "Picture Book"
        };
        
        return (
            <StoryDetail
                title={story.title}
                author={story.author}
                cover={story.cover}
                reads={story.reads}
                description={story.description}
                genre={story.genre}
                onBack={() => navigate(-1)}
            />
        );
    };

    
    
    return (
      <>
         {/* Hide navbar on auth pages */}
         {!isAuthPage && (
          <>
      {/* Show LandingNavbar on home page when not logged in */}
      {isLandingPage && !currentUser ? (
                <LandingNavbar />
            ) : (
        <Navbar 
          onNavigateHome={navigateToHome}
          onNavigateStorybook={navigateToStorybook}
          onNavigateFeaturedStories={navigateToFeaturedStories} 
        />
    )}
        </>
        )}
        <div className="main-content">
          <Routes>
            {/* Home is now the landing page at root path */}
            <Route path="/" element={
  <Home
    onNavigateHome={navigateToHome}
    onNavigateStorybook={navigateToStorybook}
    onNavigateSignup={navigateToSignup}
    onNavigateChapterBook={navigateToChapterBook}
    onNavigateGraphicNovel={navigateToGraphicNovel}
    onNavigatePictureBook={navigateToPictureBook}
    onNavigateFeaturedStories={navigateToFeaturedStories}
  />
} />

            {/* Keep /home as an alias to root for any existing bookmarks/links */}
            <Route path="/home" element={
  <Home
    onNavigateHome={navigateToHome}
    onNavigateStorybook={navigateToStorybook}
    onNavigateSignup={navigateToSignup}
    onNavigateChapterBook={navigateToChapterBook}
    onNavigateGraphicNovel={navigateToGraphicNovel}
    onNavigatePictureBook={navigateToPictureBook}
    onNavigateFeaturedStories={navigateToFeaturedStories}
  />
} />

            <Route path="/login" element={<Login />} />
            <Route path="/story/:storyId" element={<StoryDetailWrapper />} />
            <Route path="/account" element={<Account />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/signup-educator" element={<SignupEducator />} />
            
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/teacher" element={<TeacherDashboard />} />
            <Route path="/teacher/assignments" element={<TeacherAssignments />} />
            <Route path="/teacher/feedback" element={<TeacherFeedback />} />
            <Route path="/teacher/classes" element={<TeacherClasses />} />
            <Route path="/teacher/class/:classId" element={<ClassDetail />} />
            <Route path="/teacher/class/:classId/student/:studentId" element={<StudentSubmissions />} />
            <Route path="/teacher/class/:classId/story/:storyId" element={<StoryViewer />} />
            <Route path="/teacher/class/:classId/assignment/:assignmentId" element={<AssignmentSubmissions />} />

            <Route path="/about" element={<About />} />
<Route path="/contact" element={<Contact />} />
<Route path="/terms-of-service" element={<TermsOfService />} />

<Route path="/for-parents" element={<ForParents />} />
<Route path="/for-teachers" element={<ForTeachers />} />

<Route path="/my-stories" element={<MyStories />} />  {/* ← ADD THIS */}
            
            <Route path="/profile" element={
                <ProtectedRoute>
                    <Profile />
                </ProtectedRoute>
            } />

<Route path="/resources" element={<FreeResources />} />
<Route path="/free-resources" element={<FreeResources />} />

<Route path="/test-generator" element={<TestDataGenerator />} />

            <Route path="/write" element={<RegularBookEditor />} />
            <Route path="/featured-stories" element={<FeaturedStories />} />
            <Route path="/read-story/:storyId" element={<StoryReader />} />
            <Route path="/prompts" element={<Prompts />} />

            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
<Route path="/terms" element={<TermsService />} />
<Route path="/community-guidelines" element={<CommunityGuidelines />} />
<Route path="/safety" element={<SafetyCenter />} />
<Route path="/help" element={<HelpSupport />} />

            {/* Story Planner route */}
            <Route path="/enhanced-story-planner" element={
  <EnhancedStoryPlanner onClose={() => navigate('/storybook')} />
} />

<Route path="/cover-designer" element={
  <CoverDesigner 
    storyTitle="My Story"
    authorName="Author Name"
    onSave={(data) => console.log('Cover saved:', data)}
    onClose={() => navigate(-1)}
  />
} />
              

            {/* Grammar section with properly structured routes */}
            <Route path="/grammar" element={<Grammar />} />
            <Route path="/grammar/flashcards" element={<GrammarFlashcards onNavigateHome={navigateToHome} />} />
            <Route path="/grammar/detective" element={<Detective onNavigateHome={navigateToHome} />} />
            <Route path="/grammar/defender" element={<GrammarDefender />} />
            
            {/* Vocabulary section with nested routes */}
            <Route path="/vocabulary" element={<Vocabulary />} />
            <Route path="/vocabulary-flashcards" element={<VocabularyFlashcards onNavigateHome={navigateToHome} />} />
            <Route path="/vocabulary-memory" element={<VocabularyMemory onNavigateHome={navigateToHome} />} />
            
            {/* Crossword routes with proper props */}
            <Route path="/crosswords" element={<CrosswordModule onNavigateHome={navigateToHome} />} />
            <Route path="/crosswords/:puzzleId" element={<SinglePuzzle />} />
            
            {/* Keep the old route for backward compatibility */}
            <Route path="/crossword-puzzle" element={<CrosswordModule onNavigateHome={navigateToHome} />} />

            <Route path="/word-factory" element={<WordFactory 
                theme={{
                  primary: '#FF6B35',
                  secondary: '#9B59B6', 
                  accent: '#F39C12',
                  success: '#2ECC71'
                }}
                onApplyWord={(word) => {
                  console.log('Word created:', word);
                }}
            />} />

            <Route path="/games" element={<GamesHub />} />
            <Route path="/dragon-racing" element={<DragonRaceTyping />} />
            
            {/* Updated Storybook section */}
            <Route path="/storybook" element={
              <Storybook 
                onNavigateChapterBook={navigateToChapterBook}
                onNavigateGraphicNovel={navigateToGraphicNovel}
                onNavigatePictureBook={navigateToPictureBook}
                onNavigateStoryPlanner={navigateToStoryPlanner}
                onNavigateStoryStarter={navigateToStoryStarter}
                onNavigateChapterMaster={navigateToChapterMaster}
                onNavigateDailyWriter={navigateToDailyWriter}
                onNavigateStoryArcBuilder={navigateToStoryArcBuilder}
              />
            } />

<Route path="/my-cards" element={<MyCards />} />
            
            {/* Individual writing tool routes */}
            <Route path="/story-starter" element={
              <StoryStarter 
                theme={{
                  primary: '#FF6B35',
                  secondary: '#9B59B6', 
                  accent: '#F39C12',
                  success: '#2ECC71',
                  pink: '#E91E63',
                  blue: '#3498DB' 
                }}
                onApplyStarter={(starter) => {
                  console.log('Applied starter:', starter);
                  navigate('/regular-book-editor');
                }}
              />
            } />
            
            <Route path="/chapter-master" element={
              <ChapterMaster 
                theme={{
                  primary: '#FF6B35',
                  secondary: '#9B59B6', 
                  accent: '#F39C12',
                  success: '#2ECC71'
                }}
                book={[
                  { id: 1, content: 'Sample chapter content...', title: 'Chapter 1' },
                  { id: 2, content: '', title: 'Chapter 2' }
                ]}
                currentPageIndex={0}
                onUpdateBook={(book) => console.log('Updated book:', book)}
                onNavigateToPage={(index) => console.log('Navigate to page:', index)}
              />
            } />
            
            <Route path="/daily-writer" element={
              <DailyWriter 
                theme={{
                  primary: '#FF6B35',
                  secondary: '#9B59B6', 
                  accent: '#F39C12',
                  success: '#2ECC71'
                }}
                totalWordCount={500}
                onApplyPrompt={(prompt) => {
                  console.log('Applied prompt:', prompt);
                  navigate('/regular-book-editor');
                }}
              />
            } />

            <Route path="/story-arc-builder" element={<StoryArcBuilder />} />
            
            {/* Book editor routes */}
            <Route path="/regular-book-editor" element={<RegularBookEditor />} />
            <Route path="/graphic-novel-editor" element={<GraphicNovelEditor />} />
            <Route path="/picture-book-editor" element={<PictureBookEditor />} />

            <Route path="/for-schools" element={<ForSchools />} />
<Route path="/about" element={<ForSchools />} />

<Route path="/blog" element={<BlogList />} />
<Route path="/blog/:postId" element={<BlogPost />} />

<Route path="/writer/:userId" element={<AuthorProfile />} />

          </Routes>
        </div>
        {!isAuthPage && <Footer />}
      </>
    );
};

// Updated App component with ProgressProvider
const App: React.FC = () => {
  return (
    <AuthProvider>
      <ProgressProvider>
        <StoryProvider>  {/* ← ADD THIS */}
          <Router>
            <NavigationHandler />
          </Router>
        </StoryProvider>  {/* ← ADD THIS */}
      </ProgressProvider>
    </AuthProvider>
  );
};

export default App;
